import { useEffect, useRef, useState, type ChangeEvent, type RefObject } from "react";
import { Loader2, Save } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  CardPickupSection,
  type CardPickupSettings,
} from "@/components/settings/general-policies/CardPickupSection";
import { CardAssetsSection } from "@/components/settings/general-policies/CardAssetsSection";
import {
  CirculationSettingsSection,
  type CirculationSettings,
} from "@/components/settings/general-policies/CirculationSettingsSection";
import {
  PrintLayoutSection,
  type RegistrationFieldCatalogEntry,
} from "@/components/settings/general-policies/PrintLayoutSection";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DEFAULT_LIBRARY_CARD_PRINT_SETTINGS,
  mergeLibraryCardPrintSettings,
  type LibraryCardPrintSettings,
} from "@/lib/libraryCardPrintConfig";
import api from "@/lib/api";
import { registrationApi, type FormFieldConfig } from "@/lib/api/registration";
import { patronTypesApi } from "@/lib/api/patron-types";

const TOP_LEVEL_KEYS = new Set([
  "email",
  "password",
  "confirmPassword",
  "firstName",
  "lastName",
  "phone",
]);

async function buildRegistrationFieldCatalog(): Promise<RegistrationFieldCatalogEntry[]> {
  const byKey = new Map<string, RegistrationFieldCatalogEntry>();
  const add = (field: FormFieldConfig) => {
    const name = field.name?.trim();
    if (!name || TOP_LEVEL_KEYS.has(name) || byKey.has(name)) return;
    byKey.set(name, {
      key: name,
      label: field.label?.trim() || name,
      hint: `Registration form field (${field.type}).`,
    });
  };

  const general = await registrationApi.getAdminRegistrationConfig();
  general.formFields.forEach(add);
  const generalFieldIds = new Set(general.formFields.map((f) => f.id));

  let patronTypes: Awaited<ReturnType<typeof patronTypesApi.getAll>> = [];
  try {
    patronTypes = await patronTypesApi.getAll();
  } catch {
    /* non-blocking */
  }

  await Promise.all(
    patronTypes.map(async (pt) => {
      try {
        const ptCfg = await registrationApi.getAdminRegistrationConfig(pt.id);
        ptCfg.formFields.filter((f) => !generalFieldIds.has(f.id)).forEach(add);
      } catch {
        /* ignore */
      }
    })
  );

  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
}

const GeneralPoliciesSettingsPage = () => {
  const { tenant, isLoading: tenantLoading } = useTenant();
  const { isTenantAdmin } = useAccessControl();
  const [settings, setSettings] = useState<CardPickupSettings>({
    cardPickupFineEnabled: true,
    cardPickupFineDays: 7,
    cardPickupFineAmount: 0,
  });
  const [circSettings, setCircSettings] = useState<CirculationSettings>({
    allowCrossBranchCheckout: true,
    blockCheckoutFineThreshold: null,
    gracePeriodDays: 0,
    autoRenewalEnabled: false,
    holdShelfDays: 7,
    renewalWindowDays: 3,
    renewalLimitScope: "per_patron",
  });
  const [libraryCardPrint, setLibraryCardPrint] = useState<LibraryCardPrintSettings>(
    DEFAULT_LIBRARY_CARD_PRINT_SETTINGS
  );
  const [registrationFieldCatalog, setRegistrationFieldCatalog] = useState<
    RegistrationFieldCatalogEntry[]
  >([]);
  const [stampUrl, setStampUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const stampInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tenant?.id) void loadSettings();
    else if (!tenantLoading) setLoading(false);
  }, [tenant?.id, tenantLoading]);

  const loadSettings = async () => {
    if (!tenant?.id) return;
    try {
      setLoading(true);
      const [settingsRes, assetsRes, catalog] = await Promise.all([
        api.get(`/tenants/${tenant.id}`),
        api.get(`/tenants/${tenant.id}/card-assets`).catch(() => ({ data: {} })),
        buildRegistrationFieldCatalog().catch(() => [] as RegistrationFieldCatalogEntry[]),
      ]);
      setRegistrationFieldCatalog(catalog);
      const tenantSettings = (settingsRes.data.settings as Record<string, unknown>) || {};
      setSettings({
        cardPickupFineEnabled: tenantSettings.cardPickupFineEnabled !== false,
        cardPickupFineDays: (tenantSettings.cardPickupFineDays as number) || 7,
        cardPickupFineAmount: (tenantSettings.cardPickupFineAmount as number) || 0,
      });
      const circ = (tenantSettings.circulation as Record<string, unknown>) || {};
      setCircSettings({
        allowCrossBranchCheckout: circ.allowCrossBranchCheckout !== false,
        blockCheckoutFineThreshold: (circ.blockCheckoutFineThreshold as number | null) ?? null,
        gracePeriodDays: (circ.gracePeriodDays as number) ?? 0,
        autoRenewalEnabled: circ.autoRenewalEnabled === true,
        holdShelfDays: (circ.holdShelfDays as number) ?? 7,
        renewalWindowDays: (circ.renewalWindowDays as number) ?? 3,
        renewalLimitScope:
          (circ.renewalLimitScope as CirculationSettings["renewalLimitScope"]) ?? "per_patron",
      });
      setStampUrl(assetsRes.data?.stampUrl || null);
      setSignatureUrl(assetsRes.data?.signatureUrl || null);
      setLibraryCardPrint(mergeLibraryCardPrintSettings(tenantSettings.libraryCardPrint));
    } catch {
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!tenant?.id) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.get(`/tenants/${tenant.id}`);
      const currentSettings = (response.data.settings as Record<string, unknown>) || {};
      await api.put(`/tenants/${tenant.id}`, {
        settings: {
          ...currentSettings,
          cardPickupFineEnabled: settings.cardPickupFineEnabled,
          cardPickupFineDays: settings.cardPickupFineDays,
          cardPickupFineAmount: settings.cardPickupFineAmount,
          libraryCardPrint,
          circulation: {
            ...((currentSettings.circulation as object) || {}),
            allowCrossBranchCheckout: circSettings.allowCrossBranchCheckout,
            blockCheckoutFineThreshold: circSettings.blockCheckoutFineThreshold,
            gracePeriodDays: circSettings.gracePeriodDays,
            autoRenewalEnabled: circSettings.autoRenewalEnabled,
            holdShelfDays: circSettings.holdShelfDays,
            renewalWindowDays: circSettings.renewalWindowDays,
            renewalLimitScope: circSettings.renewalLimitScope,
          },
        },
      });
      setSuccess("Settings saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (
    e: ChangeEvent<HTMLInputElement>,
    kind: "stamp" | "signature",
    setUploading: (v: boolean) => void,
    inputRef: RefObject<HTMLInputElement | null>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !tenant?.id) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/tenants/${tenant.id}/card-assets/upload-${kind}`, formData);
      const assets = await api.get(`/tenants/${tenant.id}/card-assets`);
      setStampUrl(assets.data?.stampUrl || null);
      setSignatureUrl(assets.data?.signatureUrl || null);
      setSuccess(`${kind === "stamp" ? "Stamp" : "Signature"} uploaded successfully`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || `Failed to upload ${kind}`);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-slate-900">Access denied</h2>
            <p className="text-slate-600">You need Tenant Admin permissions to access this page.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const disabled = loading || !tenant?.id;

  return (
    <AppLayout>
      <PageContainer className="pb-24">
        <PageHeader
          title="General Policies"
          description="Card pickup fines, print layout, and tenant-wide circulation defaults."
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "General Policies" }]}
          showSearch={false}
          titleSize="md"
        />

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {success ? (
          <Alert className="border-emerald-200 bg-emerald-50">
            <AlertDescription className="text-emerald-700">{success}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" label="Loading settings…" />
          </div>
        ) : !tenant?.id ? (
          <Card className="p-8 text-center text-slate-600">
            No tenant context. Ensure you are on a tenant subdomain and logged in as an admin.
          </Card>
        ) : (
          <Tabs defaultValue="pickup" className="space-y-4">
            <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
              <TabsTrigger value="pickup">Card pickup</TabsTrigger>
              <TabsTrigger value="assets">Stamp & signature</TabsTrigger>
              <TabsTrigger value="print">Print layout</TabsTrigger>
              <TabsTrigger value="circulation">Circulation</TabsTrigger>
            </TabsList>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5 sm:p-6">
                <TabsContent value="pickup" className="mt-0">
                  <SectionIntro
                    title="Library card pickup fines"
                    body="Configure fines when patrons miss the physical card pickup deadline."
                  />
                  <CardPickupSection
                    settings={settings}
                    onChange={setSettings}
                    disabled={disabled}
                  />
                </TabsContent>
                <TabsContent value="assets" className="mt-0">
                  <SectionIntro
                    title="Stamp & signature"
                    body="Images printed on physical library cards for authenticity."
                  />
                  <CardAssetsSection
                    stampUrl={stampUrl}
                    signatureUrl={signatureUrl}
                    uploadingStamp={uploadingStamp}
                    uploadingSignature={uploadingSignature}
                    stampInputRef={stampInputRef}
                    signatureInputRef={signatureInputRef}
                    onStampUpload={(e) => uploadAsset(e, "stamp", setUploadingStamp, stampInputRef)}
                    onSignatureUpload={(e) =>
                      uploadAsset(e, "signature", setUploadingSignature, signatureInputRef)
                    }
                  />
                </TabsContent>
                <TabsContent value="print" className="mt-0">
                  <SectionIntro
                    title="Physical card print layout"
                    body="Control what appears on bulk-printed patron cards (A4)."
                  />
                  <PrintLayoutSection
                    settings={libraryCardPrint}
                    catalog={registrationFieldCatalog}
                    onChange={setLibraryCardPrint}
                  />
                </TabsContent>
                <TabsContent value="circulation" className="mt-0">
                  <SectionIntro
                    title="Circulation defaults"
                    body="Checkout, renewal, fine threshold, and hold shelf rules for this library."
                  />
                  <CirculationSettingsSection
                    settings={circSettings}
                    onChange={setCircSettings}
                    disabled={disabled}
                  />
                </TabsContent>
              </CardContent>
            </Card>
          </Tabs>
        )}
      </PageContainer>

      {!loading && tenant?.id ? (
        <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80">
          <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              Saves card pickup, print layout, and circulation settings together.
            </p>
            <Button onClick={handleSave} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save all settings
            </Button>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
};

function SectionIntro({ title, body }: { title: string; body: string }) {
  return (
    <div className="mb-5">
      <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-500">{body}</p>
    </div>
  );
}

export default GeneralPoliciesSettingsPage;
